import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import {
  Alert,
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AddressForm from "../../../../ui/utils/AddressForm";
import CustomDatePicker from "../../../../ui/utils/CustomDatePicker";
import { store } from "../../../../../store";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../../../shared/ui/toast/Toast";
import { getCenters } from "../../../../../redux/actions/center";
import { getSamples } from "../../../../../redux/actions/sample";
import {
  createSampleShipment,
  editSampleShipment,
} from "../../../../../redux/actions/sampleShipment";
import { showErrorAlert } from "../../../../ui/utils/AlertUtils";
import SampleSelectionSection, {
  sampleTypeLabel,
} from "./shared/SampleSelectionSection";
import SampleShipmentAudit from "./shared/SampleShipmentAudit";
import SampleShipmentSteps from "./shared/SampleShipmentSteps";
import useShipmentCenter from "./shared/useShipmentCenter";
import { cdlOperator } from "../../../../../shared/AccessHelper";
import { getSampleShipmentDestinations } from "../../../../../services/sampleSetupService";

const roundToNearest30Minutes = (time) => {
  const current = dayjs(time);
  const roundedMinutes = Math.round(current.minute() / 30) * 30;
  return current.minute(roundedMinutes).second(0).millisecond(0);
};

const combineDateAndTime = (dateValue, timeValue) => {
  const date = dayjs(dateValue);
  if (!date.isValid()) return null;

  if (typeof timeValue === "string" && /^\d{1,2}:\d{2}/.test(timeValue)) {
    const [hours, minutes] = timeValue.split(":");
    return date
      .hour(Number(hours))
      .minute(Number(minutes))
      .second(0)
      .millisecond(0)
      .toISOString();
  }

  const time = dayjs.isDayjs(timeValue) ? timeValue : dayjs(timeValue);
  if (!time.isValid()) return date.toISOString();

  return date
    .hour(time.hour())
    .minute(time.minute())
    .second(0)
    .millisecond(0)
    .toISOString();
};

const groupSamplesByPatient = (samples) => {
  const grouped = new Map();

  samples.forEach((sample) => {
    const patientKey =
      sample.patientId ||
      sample.patient?.id ||
      sample.patientCode ||
      sample.patientKey ||
      "unknown";
    const label =
      sample.patientCode ||
      sample.patientKey ||
      sample.patient?.code ||
      sample.patient?.number ||
      String(patientKey);

    if (!grouped.has(patientKey)) {
      grouped.set(patientKey, {
        selectedPatient: label,
        patientId: sample.patientId || sample.patient?.id || null,
        samples: [],
      });
    }

    const typeLabel = sampleTypeLabel(sample);

    grouped.get(patientKey).samples.push({
      selectedAnalysis: sample.analyses?.[0]?.id || null,
      selectedVisits: (sample.visits || []).map((visit) => visit.id),
      totalSamples: sample.quantity || 1,
      sampleType: typeLabel === "-" ? "" : typeLabel,
    });
  });

  return Array.from(grouped.values());
};

const SectionHeader = ({ title, description, action }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: { xs: "flex-start", sm: "center" },
      justifyContent: "space-between",
      flexDirection: { xs: "column", sm: "row" },
      gap: 1.5,
      mb: 2.5,
    }}>
    <Box>
      <Typography
        variant="h6"
        sx={{
          color: "primary.main",
          fontWeight: 700,
          lineHeight: 1.25,
        }}>
        {title}
      </Typography>
      {description && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {description}
        </Typography>
      )}
    </Box>
    {action}
  </Box>
);

const sectionSx = {
  p: { xs: 2, md: 3 },
  borderRadius: 2,
  borderColor: "divider",
};

export const ScreenSampleRequestForm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const routeCenterNumber = location.state?.centerNumber;
  const routeCenterId = location.state?.centerId;
  const initialShipment = location.state?.initialShipment;
  const routeSampleShipmentId =
    location.state?.sampleShipmentId ||
    initialShipment?.sampleShipmentId ||
    initialShipment?.id ||
    "";

  const user = useSelector((state) => state.user.user);
  const adminUser = user.role === "admin";
  const canViewDestinationAddress = cdlOperator(user);
  const centers = useSelector((state) => state.center.centers) || [];
  const samples = useSelector((state) => state.sample.samples) || [];

  const sortedCenters = React.useMemo(
    () =>
      [...centers].sort((left, right) =>
        String(left.centerNumber || "").localeCompare(
          String(right.centerNumber || ""),
          undefined,
          { sensitivity: "base" },
        ),
      ),
    [centers],
  );

  const [selectedCenterId, setSelectedCenterId] = React.useState(
    initialShipment?.centerId || routeCenterId || "",
  );
  const center = useShipmentCenter(selectedCenterId, true);
  const [selectedSamples, setSelectedSamples] = React.useState(
    initialShipment?.samples || [],
  );
  const [errorMessage, setErrorMessage] = React.useState("");
  const [timeError, setTimeError] = React.useState("");
  const [savingAction, setSavingAction] = React.useState("");
  const [requestTrackingDate, setRequestTrackingDate] = React.useState(
    initialShipment?.createdAt || initialShipment?.sendingDate || new Date(),
  );

  const [expectedShippingDate, setExpectedShippingDate] = React.useState(
    initialShipment?.expectedShippingDate
      ? new Date(initialShipment.expectedShippingDate)
      : new Date(),
  );
  const [pickUpSampleStartTime, setPickUpSampleStartTime] = React.useState(
    initialShipment?.pickUpSampleStartTime
      ? dayjs(initialShipment.pickUpSampleStartTime, "HH:mm")
      : roundToNearest30Minutes(dayjs()),
  );
  const [pickUpSampleEndTime, setPickUpSampleEndTime] = React.useState(
    initialShipment?.pickUpSampleEndTime
      ? dayjs(initialShipment.pickUpSampleEndTime, "HH:mm")
      : roundToNearest30Minutes(dayjs()).add(3, "hour"),
  );

  const [hospital, setHospital] = React.useState("");
  const [ward, setWard] = React.useState("");
  const [building, setBuilding] = React.useState("");
  const [street, setStreet] = React.useState("");
  const [floor, setFloor] = React.useState("");
  const [zone, setZone] = React.useState("");
  const [destinationHospital, setDestinationHospital] = React.useState("");
  const [destinationWard, setDestinationWard] = React.useState("");
  const [destinationBuilding, setDestinationBuilding] = React.useState("");
  const [destinationStreet, setDestinationStreet] = React.useState("");
  const [destinationZone, setDestinationZone] = React.useState("");
  const [destinationFloor, setDestinationFloor] = React.useState("");
  const [destinationZipcode, setDestinationZipcode] = React.useState("");
  const [destinationCity, setDestinationCity] = React.useState("");
  const [destinationCountry, setDestinationCountry] = React.useState("");
  const [destinationContactName, setDestinationContactName] =
    React.useState("");
  const [destinationContactPhone, setDestinationContactPhone] =
    React.useState("");
  const [destinationContactEmail, setDestinationContactEmail] =
    React.useState("");
  const [destinationOptions, setDestinationOptions] = React.useState([]);
  const [selectedDestinationId, setSelectedDestinationId] =
    React.useState("");
  const [contactName, setContactName] = React.useState("");
  const [contactPhone, setContactPhone] = React.useState("");
  const [contactEmail, setContactEmail] = React.useState("");
  const [city, setCity] = React.useState("");
  const [zipcode, setZipcode] = React.useState("");
  const [country, setCountry] = React.useState("");
  const [fieldErrors, setFieldErrors] = React.useState({});

  const selectedCenter = React.useMemo(
    () =>
      centers.find(
        (availableCenter) =>
          String(availableCenter.id) === String(selectedCenterId),
      ),
    [centers, selectedCenterId],
  );
  const centerNumber =
    selectedCenter?.centerNumber ||
    routeCenterNumber ||
    center?.centerNumber ||
    "";
  const effectiveStudyId =
    location.state?.studyId || selectedCenter?.studyId || center?.studyId;

  const applyDestination = React.useCallback((destination) => {
    setDestinationHospital(destination?.hospital || "");
    setDestinationWard(destination?.ward || "");
    setDestinationBuilding(destination?.building || "");
    setDestinationStreet(destination?.street || "");
    setDestinationZone(destination?.zone || "");
    setDestinationFloor(destination?.floor || "");
    setDestinationZipcode(destination?.zipcode || "");
    setDestinationCity(destination?.city || "");
    setDestinationCountry(destination?.country || "");
    setDestinationContactName(destination?.contactName || "");
    setDestinationContactPhone(destination?.contactPhone || "");
    setDestinationContactEmail(destination?.contactEmail || "");
  }, []);

  React.useEffect(() => {
    if (!routeCenterId && location.state?.studyId) {
      store.dispatch(getCenters(location.state.studyId));
    }
  }, [location.state?.studyId, routeCenterId]);

  React.useEffect(() => {
    if (selectedCenterId) {
      store.dispatch(getSamples());
    }
  }, [selectedCenterId]);

  React.useEffect(() => {
    let active = true;

    if (!canViewDestinationAddress || !effectiveStudyId) {
      setDestinationOptions([]);
      setSelectedDestinationId("");
      return undefined;
    }

    getSampleShipmentDestinations(effectiveStudyId)
      .then((destinations) => {
        if (!active) return;

        const nextDestinations = destinations || [];
        setDestinationOptions(nextDestinations);

        if (!initialShipment && nextDestinations.length > 0) {
          setSelectedDestinationId(nextDestinations[0].id);
          applyDestination(nextDestinations[0]);
        } else {
          setSelectedDestinationId("");
        }
      })
      .catch((error) => {
        if (!active) return;
        console.error("Failed to load sample destinations:", error);
        setDestinationOptions([]);
        setSelectedDestinationId("");
      });

    return () => {
      active = false;
    };
  }, [
    applyDestination,
    canViewDestinationAddress,
    effectiveStudyId,
    initialShipment,
  ]);

  React.useEffect(() => {
    if (initialShipment) {
      setSelectedSamples(initialShipment.samples || []);

      const address = initialShipment.address || {};
      const destination = address.destination || {};
      setHospital(address.hospital || "");
      setWard(address.ward || "");
      setBuilding(address.building || "");
      setStreet(address.street || "");
      setFloor(address.floor || "");
      setZone(address.zone || "");
      setCity(address.city || "");
      setZipcode(address.zipcode || "");
      setCountry(address.country || "");
      setContactName(address.contactName || "");
      setContactPhone(address.contactPhone || "");
      setContactEmail(address.contactEmail || "");
      setRequestTrackingDate(
        initialShipment.createdAt || initialShipment.sendingDate || new Date(),
      );
      setDestinationHospital(
        address.destinationHospital || destination.hospital || "",
      );
      setDestinationWard(address.destinationWard || destination.ward || "");
      setDestinationBuilding(
        address.destinationBuilding || destination.building || "",
      );
      setDestinationStreet(
        address.destinationStreet || destination.street || "",
      );
      setDestinationZone(address.destinationZone || destination.zone || "");
      setDestinationFloor(address.destinationFloor || destination.floor || "");
      setDestinationZipcode(
        address.destinationZipcode || destination.zipcode || "",
      );
      setDestinationCity(address.destinationCity || destination.city || "");
      setDestinationCountry(
        address.destinationCountry || destination.country || "",
      );
      setDestinationContactName(destination.contactName || "");
      setDestinationContactPhone(destination.contactPhone || "");
      setDestinationContactEmail(destination.contactEmail || "");
    } else if (center && selectedCenterId) {
      setHospital(center.sampleHospital || "");
      setWard(center.sampleWard || "");
      setBuilding(center.sampleBuilding || "");
      setStreet(center.sampleStreet || "");
      setZone(center.sampleZone || "");
      setCity(center.city || "");
      setFloor(center.sampleFloor || "");
      setZipcode(center.zipcode || "");
      setCountry(center.country || "");
      setContactPhone(center.contactPhone || "");
      setContactName(
        center.contactName ? center.contactName.replace(/,\s*/, " ") : "",
      );
      setContactEmail(center.contactEmail || "");
      setRequestTrackingDate(new Date());
    }
  }, [initialShipment, center, selectedCenterId]);

  const handleDestinationChange = (destinationId) => {
    const destination = destinationOptions.find(
      (option) => String(option.id) === String(destinationId),
    );
    if (!destination) return;

    setSelectedDestinationId(destination.id);
    applyDestination(destination);
  };

  const handleRequestTrackingChange = React.useCallback((nextTracking) => {
    setContactName(nextTracking.name || "");
    setContactEmail(nextTracking.email || "");
    setRequestTrackingDate(nextTracking.date || new Date());
  }, []);

  const handleStartTimeChange = (newValue) => {
    setPickUpSampleStartTime(newValue);
    if (newValue) {
      setPickUpSampleEndTime(dayjs(newValue).add(3, "hour"));
    }
    setTimeError("");
  };

  const handleEndTimeChange = (newValue) => {
    if (
      pickUpSampleStartTime &&
      newValue &&
      dayjs(newValue).isBefore(dayjs(pickUpSampleStartTime).add(1, "hour"))
    ) {
      setTimeError(t("endTimeAfterStart"));
      return;
    }

    setPickUpSampleEndTime(newValue);
    setTimeError("");
  };

  const handleShipmentAction = async (targetStatus) => {
    let message = "";

    if (!selectedCenterId) {
      message = "Please select a center.";
    } else if (selectedSamples.length === 0) {
      message = "Please add at least one sample.";
    }

    if (message) {
      setErrorMessage("");
      showErrorToast(message);
      return;
    }

    setSavingAction(targetStatus);
    setErrorMessage("");

    const shipmentData = {
      shipmentNumber: "001",
      centerId: selectedCenterId,
      centerNumber,
      user,
      samples: selectedSamples,
      patients: groupSamplesByPatient(selectedSamples),
      expectedShippingDate,
      pickUpSampleStartTime: dayjs(pickUpSampleStartTime).format("HH:mm"),
      pickUpSampleEndTime: dayjs(pickUpSampleEndTime).format("HH:mm"),
      address: {
        hospital,
        ward,
        building,
        street,
        floor,
        zone,
        destination: {
          hospital: destinationHospital,
          ward: destinationWard,
          building: destinationBuilding,
          street: destinationStreet,
          zone: destinationZone,
          floor: destinationFloor,
          zipcode: destinationZipcode,
          city: destinationCity,
          country: destinationCountry,
          contactName: destinationContactName,
          contactPhone: destinationContactPhone,
          contactEmail: destinationContactEmail,
        },
        city,
        zipcode,
        country,
        contactName,
        contactPhone,
        contactEmail,
        centerNumber,
      },
    };

    const pickupAddress = {
      building: shipmentData.address.building,
      centerNumber: shipmentData.address.centerNumber,
      city: shipmentData.address.city,
      contactEmail: shipmentData.address.contactEmail,
      contactName: shipmentData.address.contactName,
      contactPhone: shipmentData.address.contactPhone,
      country: shipmentData.address.country,
      floor: shipmentData.address.floor,
      hospital: shipmentData.address.hospital,
      street: shipmentData.address.street,
      ward: shipmentData.address.ward,
      zipcode: shipmentData.address.zipcode,
    };
    const destinationAddress = canViewDestinationAddress
      ? {
          hospital: shipmentData.address.destination.hospital,
          ward: shipmentData.address.destination.ward,
          building: shipmentData.address.destination.building,
          street: shipmentData.address.destination.street,
          zone: shipmentData.address.destination.zone,
          floor: shipmentData.address.destination.floor,
          zipcode: shipmentData.address.destination.zipcode,
          city: shipmentData.address.destination.city,
          country: shipmentData.address.destination.country,
          contactName: shipmentData.address.destination.contactName,
          contactPhone: shipmentData.address.destination.contactPhone,
          contactEmail: shipmentData.address.destination.contactEmail,
        }
      : null;

    const sampleShipmentPayload = {
      centerId: selectedCenterId,
      pickupAddress,
      destinationAddress,
      pickupDatetimeStart: combineDateAndTime(
        shipmentData.expectedShippingDate,
        shipmentData.pickUpSampleStartTime,
      ),
      pickupDatetimeEnd: combineDateAndTime(
        shipmentData.expectedShippingDate,
        shipmentData.pickUpSampleEndTime,
      ),
      createdByUserId: user.id,
      createdAt: requestTrackingDate,
      status: targetStatus,
      sampleIds: selectedSamples.map((sample) => sample.id),
    };

    try {
      const response = routeSampleShipmentId
        ? await store.dispatch(
            editSampleShipment(routeSampleShipmentId, sampleShipmentPayload),
          )
        : await store.dispatch(createSampleShipment(sampleShipmentPayload));

      showSuccessToast(
        targetStatus === "requested"
          ? t("sampleShipmentRequested", "Sample shipment requested")
          : t("sampleShipmentDraftSaved", "Sample shipment draft saved"),
      );
      const detailReference = response.data.reference;

      navigate(`/sample-shipments/${encodeURIComponent(detailReference)}`, {
        state: {
          ...location.state,
          sampleShipmentId: response.data.id,
          shipment: {
            ...shipmentData,
            reference: detailReference,
            status: response.data.status || targetStatus,
          },
        },
      });
    } catch (error) {
      console.error("Error creating sample shipment:", error);
      setErrorMessage(
        "Failed to create the sample shipment. Please try again.",
      );
      showErrorAlert("Failed to create the sample shipment. Please try again.");
    } finally {
      setSavingAction("");
    }
  };

  return (
    <Grid item xs={12}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <Alert
          severity="warning"
          variant="outlined"
          sx={{
            alignItems: "center",
            py: 0.5,
            "& .MuiAlert-message": {
              py: 0.25,
              fontSize: "0.875rem",
            },
          }}>
          {t("formCompletionNotice")}{" "}
          <Box component="span" sx={{ fontWeight: 700 }}>
            {t("requestEffectiveNotice")}
          </Box>
        </Alert>

        <SampleShipmentSteps
          activeStep={0}
          status={
            routeSampleShipmentId
              ? String(initialShipment?.status || "draft").trim().toLowerCase()
              : "draft"
          }
        />

        <Paper variant="outlined" sx={sectionSx}>
          <SectionHeader
            title={t("samplesAddress")}
            description={
              canViewDestinationAddress
                ? t("sampleAddress", "Pickup & Destination Address")
                : t("pickupAddress", "Pickup Address")
            }
          />
          <Grid container spacing={2}>
            {!routeCenterId && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>{t("center")}</InputLabel>
                  <Select
                    value={selectedCenterId}
                    label={t("center")}
                    onChange={(event) => {
                      setSelectedCenterId(event.target.value);
                      setSelectedSamples([]);
                    }}>
                    {sortedCenters.map((availableCenter) => (
                      <MenuItem
                        key={availableCenter.id}
                        value={availableCenter.id}>
                        {availableCenter.centerNumber}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            <AddressForm
              hospital={hospital}
              setHospital={setHospital}
              ward={ward}
              setWard={setWard}
              building={building}
              setBuilding={setBuilding}
              street={street}
              setStreet={setStreet}
              floor={floor}
              setFloor={setFloor}
              zone={zone}
              setZone={setZone}
              destinationHospital={destinationHospital}
              setDestinationHospital={setDestinationHospital}
              destinationWard={destinationWard}
              setDestinationWard={setDestinationWard}
              destinationBuilding={destinationBuilding}
              setDestinationBuilding={setDestinationBuilding}
              destinationStreet={destinationStreet}
              setDestinationStreet={setDestinationStreet}
              destinationZone={destinationZone}
              setDestinationZone={setDestinationZone}
              destinationFloor={destinationFloor}
              setDestinationFloor={setDestinationFloor}
              destinationZipcode={destinationZipcode}
              setDestinationZipcode={setDestinationZipcode}
              destinationCity={destinationCity}
              destinationCountry={destinationCountry}
              destinationContactName={destinationContactName}
              setDestinationContactName={setDestinationContactName}
              destinationContactPhone={destinationContactPhone}
              setDestinationContactPhone={setDestinationContactPhone}
              destinationContactEmail={destinationContactEmail}
              setDestinationContactEmail={setDestinationContactEmail}
              destinationOptions={destinationOptions}
              selectedDestinationId={selectedDestinationId}
              onDestinationChange={handleDestinationChange}
              zipcode={zipcode}
              setZipcode={setZipcode}
              phone={contactPhone}
              setPhone={setContactPhone}
              name={contactName}
              setName={setContactName}
              email={contactEmail}
              setEmail={setContactEmail}
              fieldErrors={fieldErrors}
              center={center}
              showDestination={canViewDestinationAddress}
            />
          </Grid>
        </Paper>

        <SampleSelectionSection
          selectedSamples={selectedSamples}
          availableSamples={samples}
          centerId={selectedCenterId}
          studyId={location.state?.studyId}
          onChange={setSelectedSamples}
          title={t("samples", "Samples")}
          sectionSx={sectionSx}
        />

        <Paper variant="outlined" sx={sectionSx}>
          <SectionHeader
            title={t("transportInformation")}
            description={t("pickupExpectedDate", "Pick-up expected date")}
          />
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "320px 1fr" },
                gap: 2,
                mb: 2,
              }}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 1,
                  bgcolor: "grey.50",
                  border: "1px solid",
                  borderColor: "divider",
                }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1.5,
                  }}>
                  <CalendarMonthIcon color="primary" fontSize="small" />
                  <Typography sx={{ fontWeight: 700 }}>
                    {t("expectedShippingDate")}
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                  <CustomDatePicker
                    label={t("expectedShippingDate")}
                    name="expectedShippingDate"
                    value={expectedShippingDate}
                    onChange={setExpectedShippingDate}
                    minDate={new Date()}
                    fullWidth
                    gridSize={12}
                  />
                </Grid>
              </Box>

              <Box
                sx={{
                  p: 2,
                  borderRadius: 1,
                  bgcolor: "grey.50",
                  border: "1px solid",
                  borderColor: "divider",
                }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1,
                    mb: 1.5,
                  }}>
                  <AccessTimeIcon
                    color="primary"
                    fontSize="small"
                    sx={{ mt: 0.25 }}
                  />
                  <Box>
                    <Typography sx={{ fontWeight: 700 }}>
                      {t("pickUpSampleTimeBetween")}
                    </Typography>
                  </Box>
                </Box>

                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={5}>
                    <TimePicker
                      label={t("startTime")}
                      value={pickUpSampleStartTime}
                      ampm={false}
                      onChange={handleStartTimeChange}
                      sx={{ width: "100%" }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          error={!!timeError}
                          helperText={timeError}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Typography
                      align="center"
                      color="text.secondary"
                      sx={{ fontWeight: 700 }}>
                      {t("and")}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={5}>
                    <TimePicker
                      label={t("endTime")}
                      value={pickUpSampleEndTime}
                      ampm={false}
                      onChange={handleEndTimeChange}
                      minTime={
                        pickUpSampleStartTime
                          ? pickUpSampleStartTime.add(1, "hour")
                          : null
                      }
                      sx={{ width: "100%" }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          error={!!timeError}
                          helperText={timeError}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </LocalizationProvider>
        </Paper>

        <SampleShipmentAudit
          sectionSx={sectionSx}
          visibleSteps={["request"]}
          editable={adminUser}
          trackingValues={{
            request: {
              name: contactName,
              email: contactEmail,
              date: requestTrackingDate,
            },
          }}
          onTrackingChange={{
            request: handleRequestTrackingChange,
          }}
        />

        <Box
          sx={{
            position: "relative",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            flexDirection: { xs: "column", sm: "row" },
            pt: errorMessage ? 3 : 0,
          }}>
          {errorMessage && (
            <Typography
              color="error"
              variant="body2"
              sx={{
                position: "absolute",
                top: 0,
                left: "50%",
                transform: "translateX(-50%)",
                whiteSpace: "nowrap",
              }}>
              {errorMessage}
            </Typography>
          )}
          <Button
            variant="outlined"
            onClick={() => handleShipmentAction("draft")}
            disabled={Boolean(savingAction)}
            sx={{ minWidth: 180, minHeight: 44 }}>
            {savingAction === "draft" ? "Saving..." : "Save Draft"}
          </Button>
          <Button
            variant="contained"
            onClick={() => handleShipmentAction("requested")}
            disabled={Boolean(savingAction)}
            sx={{ minWidth: 180, minHeight: 44 }}>
            {savingAction === "requested" ? "Requesting..." : "Request"}
          </Button>
        </Box>
      </Box>
    </Grid>
  );
};

export default ScreenSampleRequestForm;
